/**
 * Pre-Training Onboarding for Inside Sales Training
 * Engages user before starting the 20-session program
 */

import { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { 
    Briefcase, Target, TrendingUp, Users, Clock, Award,
    ChevronRight, ChevronLeft, CheckCircle2, Mic, Volume2,
    BookOpen, Zap, Shield, DollarSign
} from 'lucide-react';

const ONBOARDING_STEPS = [
    {
        id: 'welcome',
        title: 'Welcome to Inside Sales Training',
        subtitle: 'Your journey to B2B sales mastery begins here'
    },
    {
        id: 'why',
        title: 'Why Inside Sales?',
        subtitle: 'Tell us what brought you here'
    },
    {
        id: 'goals',
        title: 'Your Learning Goals',
        subtitle: 'What do you want to achieve?'
    },
    {
        id: 'overview',
        title: 'Program Overview',
        subtitle: '20 sessions across 4 phases'
    },
    {
        id: 'ready',
        title: 'Ready to Begin',
        subtitle: 'Let\'s set you up for success'
    }
];

const WHY_OPTIONS = [
    { id: 'new_role', label: 'Starting a new sales role', icon: Briefcase },
    { id: 'improve', label: 'Want to improve my skills', icon: TrendingUp },
    { id: 'transition', label: 'Transitioning to inside sales', icon: Users },
    { id: 'refresh', label: 'Need a refresher course', icon: BookOpen },
    { id: 'required', label: 'Company training requirement', icon: Target },
    { id: 'curious', label: 'Curious about sales techniques', icon: Zap }
];

const GOAL_OPTIONS = [
    { id: 'close_deals', label: 'Close more deals', icon: DollarSign },
    { id: 'build_pipeline', label: 'Build a stronger pipeline', icon: TrendingUp },
    { id: 'handle_objections', label: 'Handle objections better', icon: Shield },
    { id: 'understand_industry', label: 'Understand the staffing industry', icon: Briefcase },
    { id: 'improve_calls', label: 'Improve sales calls', icon: Mic },
    { id: 'qualify_leads', label: 'Qualify leads faster', icon: Target }
];

const PHASES = [
    {
        week: 1,
        name: 'Industry Fundamentals',
        description: 'Master staffing basics and MSP landscape',
        sessions: 5,
        color: 'from-blue-500 to-cyan-500'
    },
    {
        week: 2,
        name: 'Direct Client Sales',
        description: 'Identify and engage direct buyers',
        sessions: 5,
        color: 'from-purple-500 to-pink-500'
    },
    {
        week: 3,
        name: 'Sales Process',
        description: 'Master the 4-part framework',
        sessions: 5,
        color: 'from-amber-500 to-orange-500'
    },
    {
        week: 4,
        name: 'Decision-Makers',
        description: 'Advanced stakeholder management',
        sessions: 5,
        color: 'from-emerald-500 to-teal-500'
    }
];

const PreTrainingOnboarding = ({ onComplete, userName }) => {
    const [currentStep, setCurrentStep] = useState(0);
    const [selectedWhy, setSelectedWhy] = useState([]);
    const [selectedGoals, setSelectedGoals] = useState([]);

    const step = ONBOARDING_STEPS[currentStep];
    const progress = ((currentStep + 1) / ONBOARDING_STEPS.length) * 100;

    const canProceed = () => {
        if (step.id === 'why') return selectedWhy.length > 0;
        if (step.id === 'goals') return selectedGoals.length > 0;
        return true;
    };

    const handleNext = () => {
        if (currentStep < ONBOARDING_STEPS.length - 1) {
            setCurrentStep(prev => prev + 1);
        } else {
            onComplete({ selectedWhy, selectedGoals });
        }
    };

    const handleBack = () => {
        if (currentStep > 0) {
            setCurrentStep(prev => prev - 1);
        }
    };

    const toggleSelection = (id, selected, setSelected) => {
        if (selected.includes(id)) {
            setSelected(selected.filter(item => item !== id));
        } else {
            setSelected([...selected, id]);
        }
    };

    return (
        <div className="min-h-screen bg-[hsl(240,10%,4%)] text-white flex flex-col" data-testid="pre-training-onboarding">
            {/* Progress Bar */}
            <div className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-xl border-b border-white/10">
                <div className="max-w-4xl mx-auto px-6 py-4">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-mono tracking-widest text-white/60 uppercase">
                            Step {currentStep + 1} of {ONBOARDING_STEPS.length}
                        </span>
                        <span className="text-xs font-mono tracking-widest text-white/60">
                            {step.title}
                        </span>
                    </div>
                    <Progress value={progress} className="h-1 bg-white/10" />
                </div>
            </div>

            {/* Main Content */}
            <div className="flex-1 flex items-center justify-center pt-20 pb-32 px-6">
                <div className="max-w-4xl w-full">
                    {/* Step: Welcome */}
                    {step.id === 'welcome' && (
                        <div className="text-center space-y-8 animate-in fade-in duration-500">
                            <div className="inline-flex p-6 rounded-3xl bg-gradient-to-br from-indigo-500/20 to-purple-500/20 border border-indigo-500/30">
                                <Briefcase className="w-16 h-16 text-indigo-400" />
                            </div>
                            
                            <div className="space-y-4">
                                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter">
                                    Welcome{userName ? `, ${userName}` : ''}
                                </h1>
                                <p className="text-xl text-white/70 max-w-2xl mx-auto leading-relaxed">
                                    You&apos;re about to embark on a comprehensive journey to master B2B inside sales. 
                                    This isn&apos;t just another training—it&apos;s an interactive, voice-based experience 
                                    designed to transform how you sell.
                                </p>
                            </div>

                            <div className="grid grid-cols-3 gap-4 max-w-xl mx-auto pt-4">
                                <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                                    <Clock className="w-6 h-6 text-blue-400 mx-auto mb-2" />
                                    <p className="text-2xl font-bold">20</p>
                                    <p className="text-xs text-white/60">Sessions</p>
                                </div>
                                <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                                    <Target className="w-6 h-6 text-emerald-400 mx-auto mb-2" />
                                    <p className="text-2xl font-bold">4</p>
                                    <p className="text-xs text-white/60">Phases</p>
                                </div>
                                <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                                    <Award className="w-6 h-6 text-amber-400 mx-auto mb-2" />
                                    <p className="text-2xl font-bold">1</p>
                                    <p className="text-xs text-white/60">Certification</p>
                                </div>
                            </div>

                            <div className="flex items-center justify-center gap-3 pt-4 text-sm text-white/60">
                                <Mic className="w-4 h-4" />
                                <span>100% Voice-Based Interactive Training</span>
                            </div>
                        </div>
                    )}

                    {/* Step: Why Inside Sales */}
                    {step.id === 'why' && (
                        <div className="space-y-8 animate-in fade-in duration-500">
                            <div className="text-center space-y-4">
                                <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">
                                    Why Inside Sales?
                                </h1>
                                <p className="text-white/70 max-w-xl mx-auto">
                                    Understanding your motivation helps us personalize your learning experience. 
                                    Select all that apply.
                                </p>
                            </div>

                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                {WHY_OPTIONS.map(option => {
                                    const Icon = option.icon;
                                    const isSelected = selectedWhy.includes(option.id);
                                    return (
                                        <button
                                            key={option.id}
                                            onClick={() => toggleSelection(option.id, selectedWhy, setSelectedWhy)}
                                            className={`p-6 rounded-2xl border text-left transition-all duration-200 ${
                                                isSelected 
                                                    ? 'bg-indigo-500/20 border-indigo-500/50 ring-2 ring-indigo-500/30'
                                                    : 'bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20'
                                            }`}
                                            data-testid={`why-option-${option.id}`}
                                        >
                                            <Icon className={`w-6 h-6 mb-3 ${isSelected ? 'text-indigo-400' : 'text-white/60'}`} />
                                            <p className={`font-medium ${isSelected ? 'text-white' : 'text-white/80'}`}>
                                                {option.label}
                                            </p>
                                            {isSelected && (
                                                <CheckCircle2 className="w-5 h-5 text-indigo-400 absolute top-3 right-3" />
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Step: Learning Goals */}
                    {step.id === 'goals' && (
                        <div className="space-y-8 animate-in fade-in duration-500">
                            <div className="text-center space-y-4">
                                <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">
                                    What Do You Want to Achieve?
                                </h1>
                                <p className="text-white/70 max-w-xl mx-auto">
                                    Select your top learning goals. We&apos;ll track your progress toward these throughout the program.
                                </p>
                            </div>

                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                {GOAL_OPTIONS.map(option => {
                                    const Icon = option.icon;
                                    const isSelected = selectedGoals.includes(option.id);
                                    return (
                                        <button
                                            key={option.id}
                                            onClick={() => toggleSelection(option.id, selectedGoals, setSelectedGoals)}
                                            className={`p-6 rounded-2xl border text-left transition-all duration-200 relative ${
                                                isSelected 
                                                    ? 'bg-emerald-500/20 border-emerald-500/50 ring-2 ring-emerald-500/30'
                                                    : 'bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20'
                                            }`}
                                            data-testid={`goal-option-${option.id}`}
                                        >
                                            <Icon className={`w-6 h-6 mb-3 ${isSelected ? 'text-emerald-400' : 'text-white/60'}`} />
                                            <p className={`font-medium ${isSelected ? 'text-white' : 'text-white/80'}`}>
                                                {option.label}
                                            </p>
                                            {isSelected && (
                                                <CheckCircle2 className="w-5 h-5 text-emerald-400 absolute top-3 right-3" />
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Step: Program Overview */}
                    {step.id === 'overview' && (
                        <div className="space-y-8 animate-in fade-in duration-500">
                            <div className="text-center space-y-4">
                                <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">
                                    Your Learning Path
                                </h1>
                                <p className="text-white/70 max-w-xl mx-auto">
                                    The program is structured into 4 phases, each building on the previous. 
                                    Complete all 20 sessions to earn your certification.
                                </p>
                            </div>

                            <div className="grid gap-4">
                                {PHASES.map((phase, index) => (
                                    <div 
                                        key={phase.week}
                                        className="flex items-center gap-6 p-6 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 transition-colors"
                                    >
                                        <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${phase.color} flex items-center justify-center flex-shrink-0`}>
                                            <span className="text-2xl font-bold text-white">{phase.week}</span>
                                        </div>
                                        <div className="flex-1">
                                            <div className="flex items-center gap-3 mb-1">
                                                <h3 className="font-bold text-lg">{phase.name}</h3>
                                                <Badge variant="secondary" className="bg-white/10 text-white/70">
                                                    {phase.sessions} sessions
                                                </Badge>
                                            </div>
                                            <p className="text-white/60">{phase.description}</p>
                                        </div>
                                        {index === 0 && (
                                            <Badge className="bg-indigo-500/20 text-indigo-400 border-indigo-500/30">
                                                Starts here
                                            </Badge>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Step: Ready to Begin */}
                    {step.id === 'ready' && (
                        <div className="text-center space-y-8 animate-in fade-in duration-500">
                            <div className="inline-flex p-6 rounded-full bg-gradient-to-br from-emerald-500/20 to-teal-500/20 border border-emerald-500/30">
                                <CheckCircle2 className="w-16 h-16 text-emerald-400" />
                            </div>
                            
                            <div className="space-y-4">
                                <h1 className="text-4xl sm:text-5xl font-bold tracking-tighter">
                                    You&apos;re All Set!
                                </h1>
                                <p className="text-xl text-white/70 max-w-2xl mx-auto leading-relaxed">
                                    Before you begin, here are a few things to know about the training experience:
                                </p>
                            </div>

                            <div className="grid md:grid-cols-3 gap-6 max-w-3xl mx-auto">
                                <div className="p-6 rounded-2xl bg-white/5 border border-white/10 text-left">
                                    <Volume2 className="w-8 h-8 text-blue-400 mb-4" />
                                    <h3 className="font-bold mb-2">Listen & Learn</h3>
                                    <p className="text-sm text-white/60">
                                        AI coach will speak to you. Content will be displayed on screen so you can follow along.
                                    </p>
                                </div>
                                <div className="p-6 rounded-2xl bg-white/5 border border-white/10 text-left">
                                    <Mic className="w-8 h-8 text-rose-400 mb-4" />
                                    <h3 className="font-bold mb-2">Speak to Practice</h3>
                                    <p className="text-sm text-white/60">
                                        When it&apos;s your turn, click to record. Speak clearly and take your time.
                                    </p>
                                </div>
                                <div className="p-6 rounded-2xl bg-white/5 border border-white/10 text-left">
                                    <Target className="w-8 h-8 text-emerald-400 mb-4" />
                                    <h3 className="font-bold mb-2">Get Feedback</h3>
                                    <p className="text-sm text-white/60">
                                        After each section, you&apos;ll receive detailed feedback on what you did well and where to improve.
                                    </p>
                                </div>
                            </div>

                            <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/20 max-w-md mx-auto">
                                <p className="text-sm text-amber-200">
                                    <strong>Tip:</strong> Use headphones and find a quiet space for the best experience.
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Navigation Footer */}
            <div className="fixed bottom-0 left-0 right-0 bg-black/80 backdrop-blur-xl border-t border-white/10">
                <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
                    <Button
                        variant="ghost"
                        onClick={handleBack}
                        disabled={currentStep === 0}
                        className="text-white/70 hover:text-white hover:bg-white/10"
                        data-testid="onboarding-back-btn"
                    >
                        <ChevronLeft className="w-4 h-4 mr-2" />
                        Back
                    </Button>

                    <div className="flex gap-2">
                        {ONBOARDING_STEPS.map((_, index) => (
                            <div 
                                key={`step-dot-${index}`}
                                className={`w-2 h-2 rounded-full transition-colors ${
                                    index === currentStep 
                                        ? 'bg-indigo-500' 
                                        : index < currentStep 
                                        ? 'bg-emerald-500' 
                                        : 'bg-white/20'
                                }`}
                            />
                        ))}
                    </div>

                    <Button
                        onClick={handleNext}
                        disabled={!canProceed()}
                        className="bg-indigo-600 hover:bg-indigo-700 text-white px-8"
                        data-testid="onboarding-next-btn"
                    >
                        {currentStep === ONBOARDING_STEPS.length - 1 ? 'Start Training' : 'Continue'}
                        <ChevronRight className="w-4 h-4 ml-2" />
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default PreTrainingOnboarding;
